// Thin fetch wrapper for the JSON API. Same-origin only — the app never
// calls anywhere else (ADR-0002).

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** The parsed JSON error body, so callers can act on structured fields
     *  (e.g. a `code` + `templates` list) instead of the raw message. */
    public body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: body !== undefined ? { 'content-type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : null,
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(res.status, typeof data.error === 'string' ? data.error : `HTTP ${res.status}`, data);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body),
  del: <T>(url: string) => request<T>('DELETE', url),
};

export interface Member {
  id: number;
  name: string;
  avatar: string;
  color: string;
  hasPin: boolean;
  role: 'admin' | 'member';
  eligibilityTagIds: number[];
  active: boolean;
}

export interface Tag {
  id: number;
  name: string;
  kind: 'category' | 'eligibility';
  rules: { spendable?: boolean; board?: string };
}

/** OR-matched member/group targeting (ADR-0040 phase 1) — coexists with
 *  the legacy AND-matched eligibilityTagIds, doesn't replace it. */
export interface EligibilityRefs {
  memberIds: number[];
  tagIds: number[];
}

export interface Task {
  id: number;
  title: string;
  baseMinutes: number;
  dueAt: string | null;
  tagIds: number[];
  eligibilityTagIds: number[];
  eligibilityRefs?: EligibilityRefs | null;
  showAtTime?: string | null;
  hideByTime?: string | null;
  /** Set once hideByTime passes; task stays open/completable (ADR-0040 phase 1). */
  missedAt?: string | null;
  currentBoostMultiplier: number;
  currentPayoutMinutes: number;
}

/** A member as offered in the shared-display attribution sheet (ADR-0049). */
export interface AttributionMember {
  id: number;
  name: string;
  avatar: string;
  color: string;
  hasPin: boolean;
}

/** A shared-display task row: the task plus who's eligible to have done it. */
export interface DisplayTask {
  id: number;
  title: string;
  currentBoostMultiplier: number;
  currentPayoutMinutes: number;
  eligibleMembers: AttributionMember[];
}

export interface ClosedTask {
  id: number;
  title: string;
  baseMinutes: number;
  status: 'done' | 'expired' | 'cancelled' | 'skipped';
  dueAt: string | null;
  completedAt: string | null;
  skippedAt: string | null;
  expiredAt: string | null;
}

export interface Template {
  id: number;
  title: string;
  baseMinutes: number;
  recurrence: { freq: 'daily' | 'weekly' | 'monthly'; interval?: number; byWeekday?: number[] } | null;
  eligibilityRefs?: EligibilityRefs | null;
  tagIds?: number[];
  visibility?: 'everyone' | 'assignee' | 'admins';
  showAtTime?: string | null;
  hideByTime?: string | null;
  manualMultiplier: number;
  active: boolean;
}

export interface Reward {
  id: number;
  title: string;
  costMinutes: number;
  active: boolean;
}

export interface LedgerEntry {
  id: number;
  memberId: number;
  deltaMinutes: number;
  type: 'earn' | 'spend' | 'reverse' | 'adjust';
  note: string;
  effectiveAt: string;
  createdAt: string;
}

export interface BoardRow {
  memberId: number;
  name: string;
  avatar: string;
  color: string;
  minutes: number;
  committedMinutes: number;
  fairness: number;
}

export interface Leaderboard {
  period: 'week' | 'month';
  from: string;
  toExclusive: string;
  boards: { board: string; rows: BoardRow[] }[];
}

export interface AppState {
  setup: boolean;
  family?: { id: number; name: string };
  members?: Member[];
}
